import { defineStore } from 'pinia';
import { axios } from '@boot/axios.js';
import { useAuthStore } from '@stores/auth';
import { useAssetsStore } from '@stores/assets';
const MAX_NOTIFICATIONS_COUNT = 30;
/**
 * Return the default state for the notifications store.
 */
function getDefaultState() {
    return {
        notifications: [],
        qualityNotifications: [],
        eventAlertNotifications: [],
        resolvedNotifications: [],
        resolvedQualityNotifications: [],
        resolvedEventAlertNotifications: [],
        showNotificationPopup: false
    };
}
/**
 * Store for managing downtime and quality notifications.
 */
export const useNotificationsStore = defineStore('notifications', {
    state: () => ({
        ...getDefaultState()
    }),
    getters: {
        /**
         * Get a notification by index.
         */
        getNotification: state => {
            return (index) => {
                return state.notifications[index];
            };
        },
        /**
         * Determine whether there are notifications.
         */
        isNotificationsEmpty: state => {
            return state.notifications.length === 0;
        },
        /**
         * Get unresolved notifications.
         */
        getUnresolvedNotifications: state => {
            return state.notifications.filter(notification => !notification.isResolved);
        },
        /**
         * Get unresolved quality notifications.
         */
        getUnresolvedQualityNotifications: state => {
            return state.qualityNotifications.filter(notification => !notification.isResolved);
        },
        /**
         * Check if there are any unresolved notifications.
         */
        hasAnyUnresolvedNotifications() {
            return (this.getUnresolvedNotifications.length > 0 ||
                this.getUnresolvedQualityNotifications.length > 0);
        },
        /**
         * Get unresolved event alert notifications
         */
        getEventAlertNotifications: state => {
            return (state.eventAlertNotifications.filter(alertNotification => !alertNotification.isResolved) ?? []);
        },
        /**
         * check if there are any unresolved event alert notifications
         * @param state notification state object
         * @returns true if there are unresolved event alert notifications, false otherwise.
         */
        hasAnyUnresolvedEventAlertNotification: state => {
            return state.eventAlertNotifications.some(notification => notification.isResolved === false);
        },
        /**
         * Get the event alert notification by event uuid
         */
        getEventNotificationByEventUUID: state => {
            return (uuid) => state.eventAlertNotifications.find(notification => notification.eventUUID === uuid);
        }
    },
    actions: {
        /**
         * Reset store state.
         */
        resetState() {
            this.$reset();
        },
        /**
         * Expose reset action for use in components.
         */
        resetStateAction() {
            this.$reset();
        },
        /**
         * Add notification to the front of the list.
         * @param notification - Notification data.
         */
        addNotificationAction(notification) {
            if (notification.assetUUID && notification.eventUUID) {
                // Check if notification already exists
                if (this.notifications.find(n => n.eventUUID === notification.eventUUID)) {
                    return;
                }
                if (this.notifications.length === MAX_NOTIFICATIONS_COUNT) {
                    this.notifications.pop();
                }
                this.notifications.push({
                    assetUUID: notification.assetUUID,
                    eventUUID: notification.eventUUID,
                    isNew: true,
                    isResolved: false,
                    receivedAt: new Date().toISOString()
                });
                this.showNotificationPopup = true;
            }
        },
        /**
         * Add quality notification to the front of the list.
         * @param notification - Quality notification data.
         */
        addQualityNotificationAction(notification) {
            if (notification.eventID &&
                notification.assetUUID &&
                notification.eventUUID) {
                // Check if notification already exists
                if (this.qualityNotifications.find(n => n.eventUUID === notification.eventUUID)) {
                    return;
                }
                if (this.qualityNotifications.length === MAX_NOTIFICATIONS_COUNT) {
                    this.qualityNotifications.pop();
                }
                this.qualityNotifications.push({
                    eventID: notification.eventID,
                    assetUUID: notification.assetUUID,
                    eventUUID: notification.eventUUID,
                    eventDefectID: notification.eventDefectID || null,
                    isResolved: false,
                    isNew: true,
                    receivedAt: new Date().toISOString()
                });
                this.showNotificationPopup = true;
            }
        },
        /**
         * Add quality notification to the front of the list.
         * @param notification - Quality notification data.
         */
        addEventAlertNotificationAction(alertNotification) {
            if (this.eventAlertNotifications.find(n => n.eventUUID === alertNotification.eventUUID)) {
                return;
            }
            if (this.eventAlertNotifications.length === MAX_NOTIFICATIONS_COUNT) {
                this.eventAlertNotifications.pop();
            }
            this.eventAlertNotifications.push({
                eventUUID: alertNotification.eventUUID,
                eventDescription: alertNotification.eventDescription,
                assetUUID: alertNotification.assetUUID,
                causeId: alertNotification.causeId,
                causeName: alertNotification.causeName,
                isResolved: false,
                isNew: true,
                receivedAt: new Date().toISOString()
            });
            this.showNotificationPopup = true;
        },
        /**
         * Load remaining unjustified downtimes for the current shift.
         * @param facilityId - Facility ID.
         */
        async loadCurrentShiftUnjustifiedNotificationsAction(facilityId) {
            try {
                const assetsStore = useAssetsStore();
                // Get the current shift unjustified downtimes
                const eventsResponse = await axios.get(`events/?facility=${facilityId}&is_major=true&has_cause=false&user_is_notified=true&current_shift_only=true&ordering=-end`);
                // Get the current shift unjustified quality events
                const qualEventsResponse = await axios.get(`quality_events/?facility=${facilityId}&has_defect=false&user_is_notified=true&current_shift_only=true&ordering=-end`);
                // Add the events to the store
                if (eventsResponse.data.count > 0) {
                    for (const event of eventsResponse.data.results) {
                        // if the asset is deleted, skip it
                        const asset = assetsStore.getAssetByUUID(event.asset_uuid);
                        if (!asset || asset.is_deleted) {
                            continue;
                        }
                        // Check if notification already exists
                        if (this.notifications.find(n => n.eventUUID === event.uuid)) {
                            continue;
                        }
                        if (this.notifications.length ===
                            MAX_NOTIFICATIONS_COUNT) {
                            this.notifications.pop();
                        }
                        this.notifications.push({
                            assetUUID: event.asset_uuid,
                            eventUUID: event.uuid,
                            isNew: false,
                            isResolved: false,
                            receivedAt: event.start
                        });
                    }
                }
                // Add the quality events to the store
                if (qualEventsResponse.data.count > 0) {
                    for (const event of qualEventsResponse.data.results) {
                        // if the asset is deleted, skip it
                        const asset = assetsStore.getAssetByUUID(event.asset_uuid);
                        if (!asset || asset.is_deleted) {
                            continue;
                        }
                        // Check if notification already exists
                        if (this.qualityNotifications.find(n => n.eventUUID === event.uuid)) {
                            continue;
                        }
                        if (this.qualityNotifications.length ===
                            MAX_NOTIFICATIONS_COUNT) {
                            this.qualityNotifications.pop();
                        }
                        this.qualityNotifications.push({
                            eventID: event.id,
                            assetUUID: event.asset_uuid,
                            eventUUID: event.uuid,
                            eventDefectID: event.defect_id,
                            isResolved: false,
                            isNew: false,
                            receivedAt: event.end
                        });
                    }
                }
                // Do not show the notification popup if there are no notifications
                this.showNotificationPopup = false;
            }
            catch (error) {
                console.error('Failed to load unjustified downtimes due to: ', error);
                throw error;
            }
        },
        /**
         * Resolve a notification.
         * @param eventUUID - The UUID of the event to resolve.
         */
        resolveNotificationAction(eventUUID) {
            // Get the username from the Pinia auth store
            const authStore = useAuthStore();
            const username = authStore.user ? authStore.user.username : '';
            // search for index of notification to resolve
            const index = this.notifications.findIndex(notification => notification.eventUUID === eventUUID);
            if (index >= 0) {
                const notification = this.notifications[index];
                notification.isResolved = true;
                notification.resolvedBy = username;
                notification.resolvedAt = new Date().toISOString();
                this.resolvedNotifications.push({ ...notification });
                if (this.resolvedNotifications.length ===
                    MAX_NOTIFICATIONS_COUNT) {
                    this.resolvedNotifications.shift();
                }
            }
        },
        /**
         * Resolve a quality notification.
         * @param eventUUID - Event UUID.
         */
        resolveQualityNotificationAction(eventUUID) {
            // Get the username from the Pinia auth store
            const authStore = useAuthStore();
            const username = authStore.user ? authStore.user.username : '';
            // search for index of notification to resolve
            const index = this.qualityNotifications.findIndex(notification => notification.eventUUID === eventUUID);
            if (index >= 0) {
                const notification = this.qualityNotifications[index];
                notification.isResolved = true;
                notification.resolvedBy = username;
                notification.resolvedAt = new Date().toISOString();
                this.resolvedQualityNotifications.push({ ...notification });
                if (this.resolvedQualityNotifications.length ===
                    MAX_NOTIFICATIONS_COUNT) {
                    this.resolvedQualityNotifications.shift();
                }
            }
        },
        /**
         * Mark the event alert notification resolved for an asset
         * @param assetId Id of the asset
         */
        dismissEventAlertNotificationByAssetIdAction(assetUUID) {
            // const notification = this.eventAlertNotifications.find(eventAlertNotification => eventAlertNotification.assetId === assetId);
            // if (notification) notification.isResolved = true;
            this.eventAlertNotifications = this.eventAlertNotifications.filter(notification => notification.assetUUID !== assetUUID);
        },
        /**
         * Dismiss a notification.
         * @param eventUUID - Event UUID.
         */
        dismissNotificationAction(eventUUID) {
            this.notifications = this.notifications.filter(notification => notification.eventUUID !== eventUUID);
        },
        /**
         * Dismiss a quality notification.
         * @param eventUUID - Event UUID.
         */
        dismissQualityNotificationAction(eventUUID) {
            this.qualityNotifications = this.qualityNotifications.filter(notification => notification.eventUUID !== eventUUID);
        },
        /**
         * Dismiss a event alert notification.
         * @param eventUUID - UUID of the event.
         */
        dismissEventAlertNotificationAction(eventUUID) {
            this.eventAlertNotifications = this.eventAlertNotifications.filter(notification => notification.eventUUID !== eventUUID);
        },
        /**
         * Mark all notifications as old.
         */
        markAllNotificationsAsOldAction() {
            this.notifications = this.notifications.map(notification => {
                notification.isNew = false;
                return notification;
            });
            this.qualityNotifications = this.qualityNotifications.map(notification => {
                notification.isNew = false;
                return notification;
            });
            this.eventAlertNotifications = this.eventAlertNotifications.map(notification => {
                notification.isNew = false;
                return notification;
            });
        },
        /**
         * Set whether to show the notification popup.
         * @param status - Show status.
         */
        showNotificationPopupAction(status) {
            this.showNotificationPopup = status;
        }
    }
});
